import { logoutSession } from './logoutSession.mjs';
// === ORION-CORE AuthContext ===
// Desplugado do Base44. Tactical Bypass ativo (MVP standalone).
// Substituir orionAuthStub pelo SDK Orion-Core real quando disponível.

import React, { createContext, useState, useContext, useEffect } from 'react';
import rtClient from '@/api/realtimeClient'; // <-- CORREÇÃO: Importar o rtClient
import { clearIdentity, getStoredSessionId } from './sessionCredential';
import { PASSWORD_CHANGE_REQUIRED_EVENT } from './passwordChangeSignal';

// ====================================================================
//  GERENCIADOR DE IDENTIDADE CENTRALIZADO (Single Source of Truth)
// ====================================================================

const IA_DEFESA_CIVIL_HOME = 'https://iadefesacivil.com.br/';

// Leitura e limpeza da credencial vivem em sessionCredential.js (FIELD-P3);
// reexportado para os consumidores existentes.
export { clearIdentity };

// ====================================================================

const isTestingModeEnabled = import.meta.env.VITE_ALLOW_TEST_ACCESS === 'true';

const orionAuthStub = {
  me: async () => ({
    id: 'orion-usr-001',
    email: 'mvp-tester@reacta.local',
    full_name: 'MVP TESTER',
    role: 'admin',
    clearance: 'FullAccess'
  }),
  logout: () => {
    // Limpeza completa do estado local antes de sair
    try {
      localStorage.removeItem('orionToken');
      // localStorage.removeItem('mockUserRole'); // Removido conforme política WYSIWYG
      // Flags usadas pelo bypass tático
      if (typeof window !== 'undefined') {
        try { delete window.overrideAuth; } catch { }
        try { delete window.userAuthorized; } catch { }
        try { delete window.simulationActive; } catch { }
        try { delete window.restrictiveGuardsDisabled; } catch { }
        try { window.currentUser = null; } catch { }
      }
    } catch { }

    // Garante navegação na mesma aba
    if (typeof window !== 'undefined' && window.location?.replace) {
      window.location.replace(IA_DEFESA_CIVIL_HOME);
    } else if (typeof window !== 'undefined') {
      window.location.href = IA_DEFESA_CIVIL_HOME;
    }
  },
  redirectToLogin: (nextUrl) => {
    const currentPath = window.location.pathname;
    let redirect = '';
    if (nextUrl && currentPath !== '/Landing' && currentPath !== '/') {
      redirect = `?next=${encodeURIComponent(nextUrl)}`;
    }
    window.location.href = `/Landing${redirect}`;
  },
  isAuthenticated: async () => {
    return window.overrideAuth === true || !!localStorage.getItem('orionToken');
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [appPublicSettings, setAppPublicSettings] = useState(null);

  useEffect(() => {
    checkAppState();
  }, []);

  const checkAppState = async () => {
    try {
      setIsLoadingPublicSettings(true);
      setAuthError(null);

      if (isTestingModeEnabled) {
        setAppPublicSettings({ id: 'orion-sci26', public_settings: { auth_required: false } });
      } else {
        const resp = await fetch('/api/apps/public');
        if (resp.ok) {
          const payload = await resp.json();
          setAppPublicSettings(payload);
        } else {
          setAppPublicSettings({ id: 'orion-sci26', public_settings: { auth_required: true } });
        }
      }
      setIsLoadingPublicSettings(false);

      if (isTestingModeEnabled && typeof window !== 'undefined') {
        window.overrideAuth = true;
        window.userAuthorized = true;
        window.simulationActive = true;
        window.restrictiveGuardsDisabled = true;
      }

      await checkUserAuth();
    } catch (error) {
      console.error('[ORION AuthContext] Unexpected error:', error);
      setAuthError({ type: 'unknown', message: error.message || 'An unexpected error occurred' });
      setIsLoadingPublicSettings(false);
      setIsLoadingAuth(false);
    }
  };

  const checkUserAuth = async (userPayload = null, sessionPayload = null) => {
    try {
      setIsLoadingAuth(true);
      setAuthError(null);
      let currentUser = userPayload;
      let currentSession = sessionPayload;

      if (!currentUser) {
        // Se userPayload não foi fornecido, tentar buscar do backend
        try {
          const sessionId = getStoredSessionId();
          if (!sessionId) {
            setUser(null);
            setIsAuthenticated(false);
            setAuthError(null);
            setIsLoadingAuth(false);
            setAuthChecked(true);
            return null;
          }

          // Credencial no header, nunca na URL (FIELD-P3): o servidor le
          // x-session-id antes da query (extractSessionIdFromReq).
          const url = '/api/auth/me';
          const meInit = { credentials: 'include', headers: { 'x-session-id': sessionId } };
          // Falha transitoria (rede instavel — ex.: pagina saindo do
          // back-forward cache do Chrome, que fecha o WebSocket em voo e
          // pode atrapalhar o fetch seguinte — ou 5xx passageiro do
          // backend) nao pode ser tratada como sessao invalida: so o 401
          // explicito significa "nao autenticado". Sem este retry, um
          // unico hiccup derrubava isAuthenticated sem limpar
          // reacta_sessionId; a tela de login reaparecia com a sessao
          // ainda valida no backend, e um novo login nativo ali criava um
          // slot funcional duplicado (webmaster-02...) por cima da sessao
          // que continuava ativa.
          let resp;
          try {
            resp = await fetch(url, meInit);
            if (!resp.ok && resp.status !== 401) {
              await new Promise((resolve) => setTimeout(resolve, 800));
              resp = await fetch(url, meInit);
            }
          } catch (networkError) {
            await new Promise((resolve) => setTimeout(resolve, 800));
            resp = await fetch(url, meInit);
          }
          if (resp.ok) {
            const payload = await resp.json();
            if (payload?.ok === false) throw new Error('api-auth-fail');
            currentUser = payload?.user || payload;
            currentSession = payload?.session || null;
            const authoritativeSessionId = currentSession?.sessionId || currentSession?.session_id || null;
            if (authoritativeSessionId) {
              localStorage.setItem('reacta_sessionId', authoritativeSessionId);
              sessionStorage.setItem('reacta_sessionId', authoritativeSessionId);
            }
          } else if (resp.status === 401) {
            // Se 401, limpar qualquer sessionId local para evitar loops
            clearIdentity();
            setUser(null);
            setIsAuthenticated(false);
            setAuthError(null);
            setIsLoadingAuth(false);
            setAuthChecked(true);
            return null;
          } else {
            throw new Error(`Backend auth check failed with status ${resp.status}`);
          }
        } catch (e) {
          if (!isTestingModeEnabled) {
            throw e;
          }
          try {
            currentUser = await orionAuthStub.me();
          } catch (inner) {
            console.error('[ORION AuthContext] both backend and stub failed:', inner);
          }
        }
      }

      setUser(currentUser || null);
      setIsAuthenticated(!!currentUser);
      // Não persiste mais a identidade. O objeto 'user' no estado do AuthContext
      // é a única fonte de verdade no frontend, e é volátil.
      // SEC-IDENTITY-01: com troca de senha pendente o servidor recusa o
      // socket (password_change_required). Não conecta até a troca; o
      // checkUserAuth do FirstAccessGate conecta depois dela.
      if (currentUser && currentUser.mustChangePassword !== true) {
        // Ação crítica: Conectar ao realtime SOMENTE APÓS ter a identidade autoritativa.
        rtClient.connect({ identity: currentUser });
        // Sprint B (Secao 33 do Canon): connect() e no-op apos o primeiro
        // handshake, entao promocao/revogacao aplicada via ROLE_ASSIGNED
        // (ver efeito abaixo) precisa de um caminho explicito para atualizar
        // a identidade local ja conectada — sem isto, getOperationSnapshot().role
        // ficava preso ao papel resolvido no login ate reconexao/F5.
        rtClient.updateIdentity(currentUser);
      }
      setAuthError(null);
      setIsLoadingAuth(false);
      setAuthChecked(true);
      return currentUser; // Retorna o usuário para o chamador
    } catch (error) {
      console.error('[ORION AuthContext] User auth check failed:', error);
      setIsLoadingAuth(false);
      setIsAuthenticated(false);
      setAuthChecked(true);
      if (error.message === 'Authentication required' || error.status === 401 || error.status === 403) {
        setAuthError({ type: 'auth_required', message: 'Authentication required' });
      } else {
        setAuthError({ type: 'unknown', message: error.message || 'An unexpected error occurred' });
      }
    }
  };

  // Sprint A: quando o papel operacional do usuario atual muda dentro da
  // operacao (atribuido/revogado por um Gestor da Operacao), o backend
  // continua sendo a unica fonte de verdade — este efeito apenas dispara um
  // novo fetch de /api/auth/me, sem cachear o papel recebido no evento.
  useEffect(() => {
    const off = rtClient.on('ROLE_ASSIGNED', (payload = {}) => {
      const myOperatorId = user?.operator_id || user?.operatorId;
      if (myOperatorId && payload.targetOperatorId === myOperatorId) {
        checkUserAuth();
      }
    });
    return () => { off && off(); };
  }, [user]);

  // SEC-IDENTITY-01: qualquer chamada recusada com 403
  // password_change_required (Client.js) relê /api/auth/me; o Layout então
  // exibe o FirstAccessGate. A pendência é sempre a que o servidor informa.
  useEffect(() => {
    const onPasswordChangeRequired = () => { checkUserAuth(); };
    window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onPasswordChangeRequired);
    return () => window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onPasswordChangeRequired);
  }, []);

  const logout = () => logoutSession({
    sessionId: getStoredSessionId(),
    clearLocal: () => {
      setUser(null);
      setIsAuthenticated(false);
      clearIdentity();
      rtClient.disconnect();
    },
    warn: message => window.alert(message),
    navigate: () => orionAuthStub.logout(),
  });

  const navigateToLogin = () => {
    orionAuthStub.redirectToLogin(window.location.href);
  };

  // A função de login foi removida deste contexto. O fluxo agora é totalmente
  // gerenciado pelo AccessGateway, que chama checkUserAuth após o login.

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      authChecked,
      logout,
      navigateToLogin,
      checkUserAuth,
      checkAppState
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
